// MASA DE PRÂNZ CU SCAUNELE EI — unde stau scaunele în jurul unui blat.
//
// UN SINGUR LOC pentru regula asta, fiindcă o cer trei: paleta (masa pusă de
// mână, `model::adaugaMobila`), mobilarea automată (`design/mobilare::living`)
// și catalogul de module, ale cărui mese au fost rescrise cu ea. Scrisă de
// trei ori, s-ar fi despărțit la prima schimbare de adâncime, și două mese din
// aceeași clădire ar fi arătat altfel.
//
// CE S-A CERUT (arhitectul, 19.10.2026): «Pentru masa de bucătărie, fa o
// opțiune de masa care vine cu toate scaunele deja ca o unitate (...) sa aibă
// scaunele înăuntru puțin ca in poza trimisa si sa fie masa deasupra sa nu se
// vadă linia de la scaun suprapusă cu masa».

/** LATURA UNUI SCAUN, cât îi dă catalogul (`PIESE_MOBILIER`, felul `scaun`).
 *  Scrisă aici ca să nu atârne de o importare circulară: `arhMobilierPlan`
 *  desenează, modulul ăsta așază. */
export const SCAUN_M = 0.4;

/** CÂT INTRĂ SCAUNUL SUB BLAT, în metri.
 *
 *  Hotărât de arhitect pe previzualizare (19.10.2026), dintre 6, 12 și 18 cm:
 *  18. Pe un scaun de 40 cm asta e 45% din el — destul cât ansamblul să se
 *  citească dintr-o privire ca o unitate, nu ca șapte piese alăturate.
 *
 *  NU E UN JOC DE DESEN, e chiar așa pe planșele de mobilare: scaunul nefolosit
 *  stă împins sub masă. De-aia locul pe care-l CERE ansamblul se socotește tot
 *  pe cutia asta strânsă (v. `cutiaAnsamblului`), nu pe scaunul tras afară. */
export const SUB_BLAT_M = 0.18;

/** CÂT LOC DE OM pe o latură de masă — distanța dintre axele a două scaune
 *  vecine. Din ea iese CÂTE scaune are o latură, deci o masă mai lungă
 *  primește de la sine mai multe.
 *
 *  0,60, nu 0,70. Cifra dintâi a fost 0,70 — cotul la cot comod de pe planșele
 *  de restaurant —, iar pe masa de catalog (1,60) dă același răspuns: două
 *  scaune pe latura lungă. Dar MESELE DIN TRONSOANELE DESENATE SUNT DE 1,20,
 *  iar acolo 0,70 taie de la două scaune la unul: un modul care avea șase
 *  locuri s-ar fi trezit cu patru, fără ca nimeni să fi cerut așa ceva.
 *  0,60 e minimul obișnuit de masă — un tacâm cu farfurie are 55–60 cm —, deci
 *  nu înghesuie, dar păstrează al doilea scaun pe o masă de 1,20. */
const LOC_DE_OM_M = 0.6;

/** O piesă așezată de regula asta, în coordonatele PLANULUI. */
export type ScaunAsezat = {
  kind: "scaun";
  /** Centrul scaunului. */
  c: [number, number];
  w: number;
  h: number;
  /** Rotirea lui: `rot_deg` spune încotro îi e SPATELE (v. `Desen`), iar un
   *  scaun stă cu spatele în afară. */
  deg: number;
};

/** Câte scaune încap pe o latură lungă `l` a mesei. Cel puțin unul: o masă
 *  îngustă tot are un capăt la care se stă. */
export const scaunePeLatura = (l: number): number =>
  Math.max(1, Math.floor(l / LOC_DE_OM_M));

/** SCAUNELE DIN JURUL UNUI BLAT, pe toate patru laturile.
 *
 *  `c` e centrul mesei, `w` × `h` blatul (nu ansamblul), `deg` unghiul ei.
 *  Rezultatul e în coordonatele planului: regula se scrie în reperul mesei —
 *  `u` pe lungul blatului, `v` pe latul lui — și se întoarce o singură dată la
 *  capăt, ca o masă pusă strâmb să-și poarte scaunele la fel de drept.
 *
 *  SCAUNUL E AȘEZAT CU CENTRUL LA `latura/2 + SCAUN/2 − SUB_BLAT`: așa muchia
 *  lui dinspre masă intră exact `SUB_BLAT_M` sub blat, oricât de mare ar fi
 *  masa. Socotit din centrul mesei ca o fracțiune, un blat lung ar fi împins
 *  scaunele afară și unul scurt le-ar fi înghițit. */
export function scauneleMesei(
  c: readonly [number, number], w: number, h: number, deg = 0,
): ScaunAsezat[] {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const inPlan = (u: number, v: number): [number, number] => [
    r3(c[0] + u * cos - v * sin),
    r3(c[1] + u * sin + v * cos),
  ];
  const out: ScaunAsezat[] = [];
  // Cele două laturi LUNGI (pe `u`), scaunele privind pe `v`.
  const nLung = scaunePeLatura(w);
  const dv = h / 2 + SCAUN_M / 2 - SUB_BLAT_M;
  for (const sens of [-1, 1] as const) {
    for (let i = 0; i < nLung; i++) {
      // Scaunele se împart egal pe latură: centrul fiecărei „felii”.
      const u = (-w / 2) + (w * (i + 0.5)) / nLung;
      out.push({ kind: "scaun", c: inPlan(u, sens * dv), w: SCAUN_M, h: SCAUN_M,
                 // Spatele în afară: în jos (0°) pentru latura de sus.
                 deg: in360(deg + (sens < 0 ? 0 : 180)) });
    }
  }
  // Cele două CAPETE (pe `v`), scaunele privind pe `u`.
  const nScurt = scaunePeLatura(h);
  const du = w / 2 + SCAUN_M / 2 - SUB_BLAT_M;
  for (const sens of [-1, 1] as const) {
    for (let i = 0; i < nScurt; i++) {
      const v = (-h / 2) + (h * (i + 0.5)) / nScurt;
      out.push({ kind: "scaun", c: inPlan(sens * du, v), w: SCAUN_M, h: SCAUN_M,
                 deg: in360(deg + (sens < 0 ? 90 : -90)) });
    }
  }
  return out;
}

/** CÂT LOC CERE ANSAMBLUL — blatul plus scaunele trase sub el, pe axele
 *  mesei. Cine caută loc pentru masă verifică pe cutia ASTA, nu pe blat:
 *  altfel ar așeza fotoliul peste scaune (v. `mobilare::living`). */
export function cutiaAnsamblului(w: number, h: number): { w: number; h: number } {
  const creste = 2 * (SCAUN_M - SUB_BLAT_M);
  return { w: w + creste, h: h + creste };
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;
const in360 = (v: number) => ((v % 360) + 360) % 360;
